import { type FC, useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { supabase } from '@/lib/api/supabase';
import type { HotelPartner } from '@/types';
import { dbToHotelPartner } from '@/types';
import { HotelCard, HotelDetailDialog } from '@/pages/components';

export const HotelPage: FC = () => {
  const [hotels, setHotels] = useState<HotelPartner[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<HotelPartner | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHotels = async (): Promise<void> => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('hotel_partners')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (error) {
          console.error('Failed to fetch hotels:', error);
          return;
        }

        setHotels((data ?? []).map(dbToHotelPartner));
      } catch (err) {
        console.error('Error fetching hotels:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchHotels();
  }, []);

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-white">Loading hotel information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      <section className="container mx-auto px-4 pt-12 pb-6 text-center text-white">
        <h1 className="text-5xl font-viking mb-3 drop-shadow-lg">Host Hotels</h1>
        <p className="max-w-2xl mx-auto text-lg drop-shadow-md">
          Stay where the action is. Browse our partner hotels and book at the Cactus Cup group rate.
        </p>
      </section>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {hotels.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Hotel information will be available soon.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {hotels.map((hotel) => (
              <HotelCard
                key={hotel.id}
                hotel={hotel}
                onSelect={setSelectedHotel}
              />
            ))}
          </div>
        )}
      </div>

      <HotelDetailDialog
        hotel={selectedHotel}
        onClose={() => setSelectedHotel(null)}
      />
    </div>
  );
};
